import React, { useEffect, useMemo, useRef, useState } from "react";
import { flushSync } from "react-dom";
import ReactWebChat, {
  Components,
  createDirectLine,
  createStore,
  hooks,
} from "botframework-webchat";

import { makeStyles } from "@mui/styles";
import {
  Avatar,
  IconButton,
  Menu,
  MenuItem,
  Paper,
  Typography,
} from "@mui/material";
import CustomCard from "./CustomCard";
import { CustomButton } from "./customButton";
import { Reactions } from "./Reactions";
import { getInitialsFromFirstNameOrFullNameAndLastName } from "../helpers/strings";
import { ACTIONS, EVENTS } from "../events/botContants";
import {
  createChatCardActionMiddleware,
  createConversationEventGuard,
  epicLinkMainFrameMiddleware,
  emitData,
  emitMobileCloseBot,
  fetchDirectLineConnection,
  getActivityConversationId,
  clearUserTranscriptActivities,
  getPlatform,
  getPublicAssetUrl,
  persistUserTranscriptActivity,
  readStoredConversation,
  readUserTranscriptActivities,
  reconnectWithIosStorageRecovery,
  getUserBrowserDetails,
  isSafariBrowser,
  isAcceptedDirectLineActivityId,
  resolveDirectLineConversationId,
  sendClientPlatformInfoAfterHandoff,
  sendClientPlatformInfoOnConnection,
  isResolvedAgeVerificationActivity,
} from "../helpers/utils";
import { isMobileQueryParamEnabled } from "../helpers/queryParams";
import {
  isNativeAppAtClose,
  redirectAfterEndChat,
} from "../helpers/endChatNavigation";
import {
  isAndroidBotMessage,
  scheduleAndroidBotScroll,
} from "../helpers/androidAutoScroll";
import {
  completeChatClose,
  createCloseChatActivity,
  resolveBotCloseChatOptions,
} from "../helpers/botActivities";
import {
  deliverChatClosedEvent,
  hasNativeSurveyLaunchBridge,
  launchValidatedSurveyAnchor,
  launchValidatedSurveyLink,
  normalizeSurveyUrl,
} from "../helpers/surveyLaunch";

const imageAssetUrl = (fileName) =>
  getPublicAssetUrl(`assets/images/${fileName}`);
const guestAvatarURL = imageAssetUrl("Guest_avatar.svg");
const hopeChatBotAvatarURL = imageAssetUrl("Hope_Agent_avatar_40x40_v2.svg");
const ucmHopeChatBotAvatarURL = imageAssetUrl("Hope2_Icon_UCM.svg");
const adventLogoURL = imageAssetUrl("advent_logo.svg");
const bubbleIndicatorSources = {
  one: imageAssetUrl("bubble1.svg"),
  two: imageAssetUrl("bubble2.svg"),
  three: imageAssetUrl("bubble3.svg"),
};
const cardActionMiddleware = createChatCardActionMiddleware();
const htmlContentTransformMiddleware = Object.freeze([
  epicLinkMainFrameMiddleware,
]);
const defaultAvatarStyles = { borderRadius: "50%", width: 32, height: 32 };
const surveyAvatarStyles = { borderRadius: "50%", width: 32, height: 32 };
const CHARACTER_LIMIT = 500;
const DEFAULT_CHARACTER_LIMIT = 500;
const INITIAL_TEXT_INPUT_PLACEHOLDER = "Please confirm your age above";
const DISABLED_TEXT_INPUT_PLACEHOLDER = "Chat unavailable due to age";
const ENABLED_TEXT_INPUT_PLACEHOLDER = "Ask about your health care";
const IRIS_HANDOFF_CODE_QUERY_PARAM = "handoff_code";
const IRIS_AUTH_TIMEOUT_MS = 20000;
const AUTHENTICATED_SESSION_STORAGE_KEY = "hope:authenticated-session";
const PENDING_SESSION_CLEANUP_STORAGE_KEY =
  "hope:pending-session-cleanup";
const AUTHENTICATED_WEB_END_CHAT_URL =
  process.env.REACT_APP_AUTHENTICATED_WEB_END_CHAT_URL ||
  process.env.REACT_APP_AUTHENTICATED_END_CHAT_URL ||
  "";

const getSessionStorage = () => {
  try {
    return window.sessionStorage;
  } catch {
    return null;
  }
};

const hasAuthenticatedSessionMarker = () => {
  try {
    return Boolean(
      getSessionStorage()?.getItem(AUTHENTICATED_SESSION_STORAGE_KEY),
    );
  } catch {
    return false;
  }
};

const markHopeSessionCleanupPending = (conversationId) => {
  try {
    getSessionStorage()?.setItem(
      PENDING_SESSION_CLEANUP_STORAGE_KEY,
      JSON.stringify({ conversationId }),
    );
  } catch {
    // Cleanup is best effort; navigation must still complete.
  }
};

const completePendingHopeSessionCleanup = () => {
  const storage = getSessionStorage();
  if (!storage) {
    return;
  }

  try {
    const pendingCleanup = storage.getItem(
      PENDING_SESSION_CLEANUP_STORAGE_KEY,
    );
    if (!pendingCleanup) {
      return;
    }

    const conversationId = JSON.parse(pendingCleanup)?.conversationId || "";
    clearUserTranscriptActivities(storage, conversationId);
    storage.removeItem(AUTHENTICATED_SESSION_STORAGE_KEY);
    storage.removeItem(PENDING_SESSION_CLEANUP_STORAGE_KEY);
    localStorage?.removeItem("conversationId");
  } catch {
    // Cleanup is best effort; Hope initialization must still complete.
  }
};

const createClientActivityID = () =>
  window.crypto?.randomUUID?.() ||
  `hope-${Date.now()}-${Math.random().toString(36).slice(2)}`;

const readIrisHandoffCode = (currentUrl = window.location.href) => {
  const url = new URL(currentUrl);
  const handoffCode =
    url.searchParams.get(IRIS_HANDOFF_CODE_QUERY_PARAM)?.trim() || "";

  if (handoffCode) {
    console.info("[IRIS handoff] handoff_code query param found.");
  } else if (url.pathname === "/auth/success") {
    console.warn(
      "[IRIS handoff] /auth/success loaded without handoff_code query param.",
    );
  }

  return handoffCode;
};

const returnToMainRouteAfterIrisHandoff = () => {
  const url = new URL(window.location.href);
  url.searchParams.delete(IRIS_HANDOFF_CODE_QUERY_PARAM);
  const nextUrl = `/${url.search}${url.hash}`;
  window.history.replaceState({}, document.title, nextUrl);
  console.info("[IRIS handoff] Returned to main route after handoff_code event.");
};

const useStyles = makeStyles(() => ({
  chatContainer: {
    display: "flex",
    flexDirection: "column",
    height: "100vh",
    minHeight: 0,
    width: "100%",
    overflow: "hidden",
    boxSizing: "border-box",
    background: "var(--CUI2-neutral-surface, #F8FAFB)",
    borderRadius: 8,
    "& .webchat, & .webchat__root, & .webchat__surface": {
      display: "flex",
      flex: "1 1 auto",
      flexDirection: "column",
      height: "100%",
      minHeight: 0,
    },
    "& .webchat__basic-transcript": {
      flex: "1 1 auto",
      minHeight: 0,
    },
    "& .webchat__chat-history-box, & .webchat__basic-transcript__scrollable": {
      flex: "1 1 auto",
      height: "100%",
      minHeight: 0,
    },
    "& .webchat__basic-transcript__scrollable": {
      overflowY: "auto",
    },
    "& .webchat__send-box__main": {
      padding: "16px 8px 6px 44px",
      gap: 9,
    },

    "& .webchat__send-box-text-box": {
      border: "1px solid #98A8B8",
      borderRadius: 4,
    },
    "& .webchat__send-box__button--align-stretch": {
      alignSelf: "flex-end !important",
    },
    "& .webchat__stacked-layout": {
      marginLeft: "16px !important",
      marginRight: "16px !important",
    },
    "& .webchat__stacked-layout--from-user .webchat__stacked-layout__avatar-gutter":
      {
        justifyContent: "flex-end",
      },
    "& .webchat__bubble--from-user": {
      marginLeft: 42,
    },
    "& .webchat__bubble--from-user .webchat__text-content": {
      fontFamily: "Montserrat",
      fontSize: 14,
      fontStyle: "normal",
      fontWeight: 500,
      lineHeight: "20px",
    },
    "& .webchat__stacked-layout__status .webchat__activity-status": {
      marginTop: 0,
      paddingTop: 0,
    },
    "& article[data-hope-age-verification-hidden='true']": {
      display: "none !important",
    },
    "& .MuiTypography-root": {
      fontFamily: "Montserrat",
    },
    "& .MuiChip-label": {
      fontFamily: "Montserrat",
    },
    borderTop: "1px solid var(--Neutral-Gray---200, #B9C5D1)",
    borderRight: "1px solid var(--Neutral-Gray---200, #B9C5D1)",
    borderLeft: "1px solid var(--Neutral-Gray---200, #B9C5D1)",
  },
  mobileChatContainer: {
    height:
      "var(--hope-visible-viewport-height, var(--mobile-webview-height, 100vh))",
    maxHeight:
      "var(--hope-visible-viewport-height, var(--mobile-webview-height, 100vh))",
    minHeight: 0,
    overflow: "hidden",
    borderRadius: 0,
    "& $chatBody": {
      paddingBottom: 0,
    },
    "& $sendBoxCounterRow": {
      paddingBottom: 8,
    },
    "@supports (height: 100dvh)": {
      height:
        "var(--hope-visible-viewport-height, var(--mobile-webview-height, 100dvh))",
      maxHeight:
        "var(--hope-visible-viewport-height, var(--mobile-webview-height, 100dvh))",
    },
  },
  mobileKeyboardOpen: {
    "& $disclaimer": {
      display: "none",
    },
  },
  chatHeader: (props) => ({
    backgroundColor: "white",
    borderBottom: "none",
    overflow: "hidden",
    background: props.isMinimized ? "#34837A" : "#E4FBFA",
    textAlign: "center",
    display: "flex",
    flexDirection: "column",
    boxSizing: "border-box",
    position: "sticky",
    top: 0,
    zIndex: 3,
    flexShrink: 0,
    boxShadow: props.isMinimized
      ? "0 1px 2px 0 rgba(25, 28, 28, 0.30), 0 1px 3px 1px rgba(25, 28, 28, 0.15)"
      : "none !important",
    width: "100%",
  }),
  mobileHeader: {
    borderRadius: 0,
    height: "auto",
  },
  mobileHeaderContainer: {
    display: "flex",
    alignItems: "center",
    justifyContent: "space-between",
    padding: 16,
    minHeight: 72,
    boxSizing: "border-box",
    "& .MuiIconButton-root": {
      color: "#192129",
    },
    "& .MuiTypography-root": {
      fontWeight: 500,
    },
  },
  mobileHeaderSide: {
    display: "flex",
    alignItems: "center",
    flexShrink: 0,
    width: 88,
  },
  mobileHeaderText: {
    display: "flex",
    flex: "1 1 auto",
    minWidth: 0,
    padding: "0 8px",
    flexDirection: "column",
    alignItems: "center",
    justifyContent: "center",
    textAlign: "center",

    "& p": {
      fontWeight: 600,
      lineHeight: "20px",
      margin: 0,
    },

    "& span": {
      color: "var(--CUI2-neutral-on-surface-variant, #454747)",
      fontSize: 12,
      lineHeight: "16px",
      overflowWrap: "anywhere",
    },
  },
  mobileHeaderIcons: {
    justifyContent: "flex-end",
  },
  accent: {
    width: "100%",
    height: 8,
    background:
      "var(--azure-chatbot-accent-background, var(--CUI2-success-success, #34837A))",
    borderRadius: "12px 12px 0px 0px",
  },
  headerFlexContainer: {
    display: "flex",
    alignItems: "center",
    padding: "4px 0 4px 16px",
    gap: 8,
    height: 64,
    boxSizing: "border-box",
  },
  headerFlexContainerMinimized: {
    background: "#F2FFFC",
    "&:hover": {
      background:
        "var(--cui-2-state-layers-on-surface-variant-opacity-008, rgba(69, 71, 71, 0.08))",
    },
  },
  headerAvatar: {
    width: 40,
    height: 40,
    borderRadius: "50%",
    flexShrink: 0,
    border: "2px solid #DAE2EA",
  },
  headerText: {
    display: "flex",
    flexDirection: "column",
    alignItems: "flex-start",
    justifyContent: "center",
    flex: "1 1 auto",
    minWidth: 0,

    "& p": {
      fontWeight: 600,
    },

    "& span": {
      color: "var(--CUI2-neutral-on-surface-variant, #454747)",
      fontSize: 12,
    },
  },
  headerIcons: {
    display: "flex",
    justifyContent: "flex-end",
    alignItems: "center",
    marginLeft: "auto",
    flexShrink: 0,
    "& .MuiIconButton-root": {
      color: "var(--CUI2-neutral-on-surface-variant, #454747)",
    },
  },
  disclaimer: {
    position: "relative",
    zIndex: 2,
    width: "100%",
    fontSize: 12,
    textAlign: "center",
    color: "#192129",
    background: " var(--White, #FFF)",
    boxShadow:
      "0 1px 2px 0 rgba(25, 28, 28, 0.30), 0 1px 3px 1px rgba(25, 28, 28, 0.15)",
    display: (props) => (props.isMinimized ? "none" : "flex"),
    flexDirection: "column",
    flexShrink: 0,
  },
  disclaimerItem: {
    borderTop: "1px solid #B9C5D1",
    padding: 16,
    margin: 0,
    fontSize: 12,
  },
  disclaimerLink: {
    color: "var(--azure-chatbot-terms-link-color, #006298)",
    textDecoration: "underline",
    fontWeight: 600,
    "&:hover": {
      textDecoration: "none",
    },
    "&:visited": {
      color: "var(--azure-chatbot-terms-link-color, #006298)",
    },
  },
  emergencyBanner: {
    color: "var(--azure-chatbot-emergency-text-color, #891511)",
  },

  chatBody: {
    flex: "1 1 auto",
    flexDirection: "column",
    justifyContent: "flex-end",
    overflow: "hidden",
    display: (props) => (props.isMinimized ? "none" : "flex"),
    background: "var(--CUI2-neutral-surface, #F8FAFB)",
    position: "relative",
    zIndex: 1,
    minHeight: 0,
    paddingBottom: 16,
  },
  irisVerificationPending: {
    alignSelf: "flex-start",
    margin: 0,
    padding: "12px 16px",
    color: "#192129",
    background: "#F0F0F0",
    borderRadius: 8,
    fontFamily: "Montserrat",
    fontSize: 14,
    lineHeight: "20px",
  },
  sendBoxContainer: {
    display: "flex",
    flexDirection: "column",
    flexShrink: 0,
  },
  sendBoxCounterRow: {
    display: "flex",
    alignItems: "center",
    gap: 8,
    padding: "0px 61px 0px 44px",
    fontSize: 14,
    lineHeight: "16px",
    fontFamily: "Montserrat",
    color: "#536E88",
    background: "var(--CUI2-neutral-surface, #F8FAFB)",
  },
  sendBoxCounterMessage: {
    color: "#B51F1A",
    fontWeight: 500,
  },
  sendBoxCounterValue: {
    marginLeft: "auto",
  },
  sendBoxCounterValueExceeded: {
    color: "#B51F1A",
    fontWeight: 500,
  },
  sendBoxBase: {
    "& .webchat__send-button .webchat__send-icon": {
      display: "none",
    },
    "& .webchat__send-button .webchat__icon-button__shade": {
      display: "none",
    },
    "& .webchat__send-button::before": {
      content: '""',
      display: "block",
      width: 40,
      height: 40,
      backgroundRepeat: "no-repeat",
      backgroundPosition: "center",
      backgroundSize: "contain",
    },
    "& .webchat__send-button[aria-disabled='true']::before, & .webchat__send-button:disabled::before":
      {
        backgroundImage: `url('${imageAssetUrl("send_disable.svg")}')`,
      },
  },
  sendBoxEmpty: {
    "& .webchat__send-button::before": {
      backgroundImage: `url('${imageAssetUrl("send_disable.svg")}')`,
    },
  },
  sendBoxHasText: {
    "& .webchat__send-button::before": {
      backgroundImage: `url('${imageAssetUrl("send.svg")}')`,
    },
  },
  sendBoxHasTextChicago: {
    "& .webchat__send-button::before": {
      backgroundImage: `url('${imageAssetUrl("chicago_send_button.svg")}')`,
    },
  },
  sendBoxTyping: {
    "& .webchat__send-box-text-box": {
      border:
        "3px solid var(--azure-chatbot-sendbox-typing-border-color, var(--CUI2-primary-primary, #006298))",
      borderRadius: 8,
    },
  },
  sendBoxExceeded: {
    "& .webchat__send-box-text-box": {
      border: "2px solid #B51F1A",
      borderRadius: 4,
    },
  },
  "@keyframes typingBubblePulse": {
    "0%, 80%, 100%": {
      opacity: 0.2,
      transform: "scale(0.7)",
    },
    "40%": {
      opacity: 1,
      transform: "scale(1)",
    },
  },
  typingIndicator: {
    display: "flex",
    alignItems: "center",
    minHeight: 20,
    marginTop: 9,
    paddingBottom: 0,
    marginBottom: 8,
  },
  typingIndicatorAvatar: {
    width: 32,
    height: 32,
    borderRadius: "50%",
    marginRight: 8,
    flexShrink: 0,
  },
  typingIndicatorButton: {
    display: "flex",
    height: 44,
    maxWidth: 267,
    padding: 12,
    flexDirection: "column",
    justifyContent: "center",
    alignItems: "center",
    gap: 10,
    borderRadius: "8px 8px 8px 0",
    backgroundColor: "var(--CUI2-neutral-surface-container-highest, #F0F1F1)",
    border: "none",
    boxSizing: "border-box",
    cursor: "default",
    pointerEvents: "none",
    appearance: "none",
    WebkitAppearance: "none",
    outline: "none",
    "&:disabled": {
      opacity: 1,
    },
  },
  typingBubbles: {
    display: "inline-flex",
    alignItems: "center",
    height: 20,
    "& img + img": {
      marginLeft: 6,
    },
  },
  typingBubble: {
    width: 8,
    height: 8,
    display: "block",
    animationName: "$typingBubblePulse",
    animationDuration: "1.2s",
    animationIterationCount: "infinite",
    animationTimingFunction: "ease-in-out",
    animationFillMode: "both",
  },
  typingBubbleOne: {
    animationDelay: "0s",
    filter: "brightness(0.7)",
  },
  typingBubbleTwo: {
    animationDelay: "0.2s",
  },
  typingBubbleThree: {
    animationDelay: "0.4s",
  },
  activityStatusContent: {
    display: "flex",
    flexDirection: "column",
    alignItems: "flex-start",
    gap: 8,
  },
  activityStatusContentFromUser: {
    alignItems: "flex-end",
  },
  iconButton: {
    padding: 8,
  },

  infoIcon: {
    height: "20px",
    width: "20px",
  },
  closeFullscreenIcon: {
    height: "24px",
    width: "24px",
  },

  menuIcon: {
    height: "24px",
    width: "24px",
    color: (props) => (props.isMobile ? "#006298" : "#192129"),
  },
  closeIcon: {
    height: "24px",
    width: "24px",
  },
  closeModalOverlay: {
    position: "fixed",
    inset: 0,
    backgroundColor: "rgba(25, 33, 41, 0.35)",
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    zIndex: 5,
    padding: "0 16px",
  },
  closeModalDialog: {
    background: "#FFFFFF",
    borderRadius: 24,
    width: "100%",
    maxWidth: 420,
    boxShadow: "0px 12px 32px rgba(25, 33, 41, 0.2)",
    fontFamily: "Montserrat",
    color: "#192129",
  },
  closeModalContent: {
    display: "flex",
    flexDirection: "column",
    alignItems: "flex-start",
    gap: 24,
    padding: "24px 24px 16px 24px",
  },
  closeModalTitle: {
    fontSize: 24,
    fontWeight: 500,
    lineHeight: "36px",
  },
  closeModalBody: {
    display: "flex",
    flexDirection: "column",
    gap: 16,
    fontSize: 18,
    lineHeight: "28px",
  },
  closeModalText: {
    margin: 0,
    fontSize: 14,
    fontWeight: 500,
    lineHeight: "20px",
  },
  closeModalLink: {
    color: "var(--azure-chatbot-close-modal-link-color, #006298)",
    textDecoration: "none",
    fontWeight: 600,
    "&:hover": {
      textDecoration: "none",
    },
    "&:visited": {
      color: "var(--azure-chatbot-close-modal-link-color, #006298)",
    },
  },
  closeModalActions: {
    padding: "16px 24px 24px 24px",
    borderTop: "1px solid #DAE2EA",
    display: "flex",
    justifyContent: "flex-end",
    "& .MuiButtonBase-root": {
      padding: "10px 24px",
    },
  },
  closeModalActionsRow: {
    display: "flex",
    gap: 16,
  },
  cancelButton: {
    "&&.MuiButton-outlined": {
      background: "#FFFFFF !important",
      border:
        "1px solid var(--azure-chatbot-close-modal-cancel-border-color, var(--Neutral-Gray---100, #DAE2EA)) !important",
      color:
        "var(--azure-chatbot-root-button-text-color, var(--Primary-Blue---500, #006298)) !important",
    },
    "&&.MuiButton-outlined:hover": {
      background: "var(--CUI2-neutral-surface, #F8FAFB) !important",
      color:
        "var(--azure-chatbot-root-button-text-color, var(--Primary-Blue---500, #006298)) !important",
    },
    "&&.MuiButton-outlined:active": {
      background: "var(--Neutral-Gray---50, #F0F1F1) !important",
      color:
        "var(--azure-chatbot-root-button-text-color, var(--Primary-Blue---500, #006298)) !important",
    },
    "&&.MuiButton-outlined:focus, &&.MuiButton-outlined.Mui-focusVisible": {
      background: "#FFFFFF !important",
      color:
        "var(--azure-chatbot-root-button-text-color, var(--Primary-Blue---500, #006298)) !important",
      outline:
        "2px solid var(--azure-chatbot-root-button-text-color, var(--Primary-Blue---500, #006298))",
      outlineOffset: 2,
    },
    "&&.MuiButton-outlined.Mui-disabled": {
      background: "#FFFFFF !important",
      border:
        "1px solid var(--Neutral-Gray---200, #B9C5D1) !important",
      color: "var(--Neutral-Gray---600, #536E88) !important",
      opacity: 1,
    },
  },
  endChatButton: {
    "&&.MuiButton-contained": {
      background:
        "var(--azure-chatbot-close-modal-end-chat-background, #006298) !important",
      color:
        "var(--azure-chatbot-close-modal-end-chat-text-color, var(--azure-chatbot-root-button-interaction-text-color, #FFF)) !important",
    },
    "&&.MuiButton-contained:hover, &&.MuiButton-contained:focus, &&.MuiButton-contained.Mui-focusVisible": {
      background:
        "var(--azure-chatbot-close-modal-end-chat-background, #006298) !important",
      color:
        "var(--azure-chatbot-close-modal-end-chat-text-color, var(--azure-chatbot-root-button-interaction-text-color, #FFF)) !important",
    },
  },
}));

const SendBoxWithCharacterCount = ({
  className,
  classes,
  isChicagoBrand,
  SendBoxComponent,
}) => {
  const [sendBoxValue] = hooks.useSendBoxValue();
  const [styleOptions] = hooks.useStyleOptions();
  const messageLength = (sendBoxValue || "").length;
  const isOverLimit = messageLength > CHARACTER_LIMIT;
  const hasText = messageLength > 0;
  const displayLimit =
    messageLength > 0 ? CHARACTER_LIMIT : DEFAULT_CHARACTER_LIMIT;
  if (styleOptions.hideSendBox) {
    console.log("hiding");

    return null;
  }
  const NextSendBox = SendBoxComponent || Components.BasicSendBox;
  const sendBoxClassName = [
    className,
    classes.sendBoxBase,
    hasText
      ? isChicagoBrand
        ? classes.sendBoxHasTextChicago
        : classes.sendBoxHasText
      : classes.sendBoxEmpty,
    hasText && !isOverLimit && classes.sendBoxTyping,
    isOverLimit && classes.sendBoxExceeded,
  ]
    .filter(Boolean)
    .join(" ");
  const counterClassName = [
    classes.sendBoxCounterValue,
    isOverLimit && classes.sendBoxCounterValueExceeded,
  ]
    .filter(Boolean)
    .join(" ");

  return (
    <div
      data-hope-composer
      className={[classes.sendBoxContainer, className]
        .filter(Boolean)
        .join(" ")}
    >
      <NextSendBox className={sendBoxClassName} />
      <div className={classes.sendBoxCounterRow}>
        {isOverLimit && (
          <span className={classes.sendBoxCounterMessage}>
            Please limit to {CHARACTER_LIMIT} characters
          </span>
        )}
        <span className={counterClassName}>
          {messageLength}/{displayLimit}
        </span>
      </div>
    </div>
  );
};

const BubbleTypingIndicator = ({ classes, avatarURL }) => {
  const [{ typingIndicator: typingIndicatorStyleSet }] = hooks.useStyleSet();
  const [direction] = hooks.useDirection();
  const localize = hooks.useLocalizer();
  const indicatorClassName = [
    typingIndicatorStyleSet + "",
    direction === "rtl" && "webchat__typing-indicator--rtl",
    classes.typingIndicator,
  ]
    .filter(Boolean)
    .join(" ");

  return (
    <div className={indicatorClassName}>
      <img
        src={avatarURL}
        alt="Hope Avatar"
        className={classes.typingIndicatorAvatar}
      />
      <button
        type="button"
        className={classes.typingIndicatorButton}
        aria-label={localize("TYPING_INDICATOR_ALT")}
        disabled
      >
        <span className={classes.typingBubbles} aria-hidden="true">
          <img
            src={bubbleIndicatorSources.one}
            alt=""
            className={`${classes.typingBubble} ${classes.typingBubbleOne}`}
          />
          <img
            src={bubbleIndicatorSources.two}
            alt=""
            className={`${classes.typingBubble} ${classes.typingBubbleTwo}`}
          />
          <img
            src={bubbleIndicatorSources.three}
            alt=""
            className={`${classes.typingBubble} ${classes.typingBubbleThree}`}
          />
        </span>
      </button>
    </div>
  );
};

export default function AzureChatBot() {
  completePendingHopeSessionCleanup();

  const currentSearchParams = new URLSearchParams(window.location.search);
  const initialIrisHandoffCode = readIrisHandoffCode();
  const isMobile = isMobileQueryParamEnabled(currentSearchParams.get("mobile"));
  const isAndroid =
    /Android/i.test(navigator.userAgent) || Boolean(window.android);
  const [isMinimized, setIsMinimized] = useState(false);
  const [isKeyboardVisible, setIsKeyboardVisible] = useState(false);
  const [isChatClosed, setIsChatClosed] = useState(false);
  const [showCloseModal, setShowCloseModal] = useState(false);
  const [showNativeCloseFallback, setShowNativeCloseFallback] = useState(false);
  const closeHandledRef = useRef(false);
  const nativeSurveyReturnCleanupRef = useRef(() => {});
  const pendingCloseOptionsRef = useRef(null);
  const [anchorEl, setAnchorEl] = useState(null);
  const [directLine, setDirectline] = useState(null);
  const [directLineConversationId, setDirectLineConversationId] = useState("");
  const [isDirectLineConnected, setIsDirectLineConnected] = useState(false);
  const activeBrand = document?.documentElement?.dataset?.brand;
  const isChicagoBrand = activeBrand === "chicago";
  const hopeChatBotAvatar = isChicagoBrand
    ? ucmHopeChatBotAvatarURL
    : hopeChatBotAvatarURL;
  const classes = useStyles({
    isChatClosed,
    isMinimized,
    isMobile,
  });
  const [sessionId, setSessionId] = useState("");
  const sessionIdRef = useRef(sessionId);
  const [surveyURL, setSurveyURL] = useState("");
  const surveyURLRef = useRef(surveyURL);
  const [isInputDisabled, setIsInputDisabled] = useState(true);
  const [inputPlaceholder, setInputPlaceholder] = useState(
    INITIAL_TEXT_INPUT_PLACEHOLDER,
  );
  const [isAgeVerificationResolved, setIsAgeVerificationResolved] =
    useState(false);
  const isAgeVerificationResolvedRef = useRef(isAgeVerificationResolved);
  isAgeVerificationResolvedRef.current = isAgeVerificationResolved;
  const [userInteracted, setUserInteracted] = useState(false);
  const [headerMessage, setHeaderMessage] = useState("AI-Enabled Virtual Assistant");
  const [clientInfo, setClientInfo] = useState({});
  const clientInfoRef = useRef(clientInfo);
  clientInfoRef.current = clientInfo;
  const shouldHideWindowControls =
    currentSearchParams.get("hideWindowControls") === "true" ||
    clientInfo.hideWindowControls === true;
  const [clientContextUrl, setClientContextUrl] = useState("");
  const [irisHandoffCode, setIrisHandoffCode] = useState(initialIrisHandoffCode);
  const [isIrisVerificationPending, setIsIrisVerificationPending] =
    useState(Boolean(initialIrisHandoffCode));
  const [slots, setSlots] = useState([]);
  const [isLocalTyping, setIsLocalTyping] = useState(false);
  const localTypingTimeoutRef = useRef(null);
  const clientPlatformInfoSentDirectLineRef = useRef(null);
  const shouldSendClientPlatformInfoRef = useRef(
    createConversationEventGuard(),
  );
  const irisHandoffSentRef = useRef(false);
  const irisHandoffPostDirectLineRef = useRef(null);
  const irisVerificationTimeoutRef = useRef(null);
  const initialIrisHandoffCodeRef = useRef(initialIrisHandoffCode);
  const directLineRef = useRef(directLine);
  const directLineConversationIdRef = useRef(directLineConversationId);
  const isAuthenticatedAndroidSessionRef = useRef(false);
  const androidBotScrollFrameRef = useRef(null);
  const persistUserActivityRef = useRef(() => {});
  const restoredTranscriptConversationRef = useRef("");
  directLineRef.current = directLine;
  directLineConversationIdRef.current = directLineConversationId;
  const chatBodyRef = useRef(null);
  const keyboardVisibleRef = useRef(false);
  const inIframe = window?.self !== window?.top;
  const isAuthenticatedAndroidSession =
    isMobile &&
    isAndroid &&
    Boolean(
      irisHandoffCode ||
      currentSearchParams.get("conversationId") ||
      hasAuthenticatedSessionMarker(),
    );
  const isAuthenticatedHopeSession = Boolean(
    isAuthenticatedAndroidSession ||
      irisHandoffCode ||
      hasAuthenticatedSessionMarker(),
  );
  isAuthenticatedAndroidSessionRef.current = isAuthenticatedAndroidSession;

  useEffect(
    () => () => {
      nativeSurveyReturnCleanupRef.current();
    },
    [],
  );

  const clearIrisVerificationTimeout = () => {
    if (irisVerificationTimeoutRef.current) {
      window.clearTimeout(irisVerificationTimeoutRef.current);
      irisVerificationTimeoutRef.current = null;
    }
  };

  useEffect(() => {
    if (!initialIrisHandoffCodeRef.current) {
      return undefined;
    }

    // Start the fail-safe when the authenticated handoff is detected so it
    // also covers token retrieval and Direct Line connection failures.
    irisVerificationTimeoutRef.current = window.setTimeout(() => {
      irisVerificationTimeoutRef.current = null;
      setIsIrisVerificationPending(false);
      console.warn(
        "[IRIS handoff] Verification did not complete within 20 seconds; restoring age verification.",
      );
    }, IRIS_AUTH_TIMEOUT_MS);

    return clearIrisVerificationTimeout;
  }, []);

  useEffect(
    () => () => {
      if (androidBotScrollFrameRef.current !== null) {
        window.cancelAnimationFrame(androidBotScrollFrameRef.current);
      }
    },
    [],
  );

  useEffect(() => {
    if (!isMobile) {
      keyboardVisibleRef.current = false;
      document.documentElement.style.removeProperty(
        "--hope-visible-viewport-height",
      );
      document.documentElement.style.removeProperty("--mobile-webview-height");
      return undefined;
    }

    let animationFrameId;
    let keyboardCloseTimeoutId;
    let keyboardViewportBaseline =
      window.visualViewport?.height || window.innerHeight;

    const clearKeyboardCloseTimeout = () => {
      if (keyboardCloseTimeoutId) {
        window.clearTimeout(keyboardCloseTimeoutId);
        keyboardCloseTimeoutId = null;
      }
    };

    const setKeyboardVisibility = (isVisible) => {
      if (keyboardVisibleRef.current !== isVisible) {
        keyboardVisibleRef.current = isVisible;
        setIsKeyboardVisible(isVisible);
      }
    };

    const scheduleKeyboardClose = () => {
      clearKeyboardCloseTimeout();
      keyboardCloseTimeoutId = window.setTimeout(() => {
        const viewportHeight =
          window.visualViewport?.height || window.innerHeight;
        const keyboardIsVisible =
          keyboardViewportBaseline - viewportHeight > 150;

        if (!keyboardIsVisible) {
          setKeyboardVisibility(false);
        }
        keyboardCloseTimeoutId = null;
      }, 400);
    };

    const resetKeyboardViewportBaseline = () => {
      clearKeyboardCloseTimeout();
      keyboardViewportBaseline =
        window.visualViewport?.height || window.innerHeight;
      setKeyboardVisibility(false);
    };

    const handleFocusIn = (event) => {
      const target = event.target;
      const isTextInput =
        target instanceof HTMLInputElement ||
        target instanceof HTMLTextAreaElement ||
        target.isContentEditable;

      if (isTextInput) {
        clearKeyboardCloseTimeout();
        setKeyboardVisibility(true);
      }
    };

    const handleFocusOut = () => {
      scheduleKeyboardClose();
    };

    const updateMobileWebViewHeight = () => {
      if (animationFrameId) {
        window.cancelAnimationFrame(animationFrameId);
      }

      animationFrameId = window.requestAnimationFrame(() => {
        const viewportHeight =
          window.visualViewport?.height || window.innerHeight;
        const visibleViewportHeight = `${Math.round(viewportHeight)}px`;
        const keyboardIsVisible =
          keyboardViewportBaseline - viewportHeight > 150;

        // Mobile browsers emit several intermediate viewport resize events
        // while the keyboard animates. Debounce the close transition so the
        // banners do not flicker during that animation. The restored viewport
        // is also important when iOS hides the keyboard while the textarea
        // remains focused after the send button is tapped.
        if (keyboardIsVisible) {
          clearKeyboardCloseTimeout();
          setKeyboardVisibility(true);
        } else if (keyboardVisibleRef.current) {
          scheduleKeyboardClose();
        } else {
          clearKeyboardCloseTimeout();
        }

        document.documentElement.style.setProperty(
          "--hope-visible-viewport-height",
          visibleViewportHeight,
        );
        document.documentElement.style.setProperty(
          "--mobile-webview-height",
          visibleViewportHeight,
        );
      });
    };

    updateMobileWebViewHeight();
    window.visualViewport?.addEventListener("resize", updateMobileWebViewHeight);
    window.visualViewport?.addEventListener("scroll", updateMobileWebViewHeight);
    window.addEventListener("resize", updateMobileWebViewHeight);
    window.addEventListener("orientationchange", resetKeyboardViewportBaseline);
    document.addEventListener("focusin", handleFocusIn);
    document.addEventListener("focusout", handleFocusOut);

    return () => {
      if (animationFrameId) {
        window.cancelAnimationFrame(animationFrameId);
      }

      window.visualViewport?.removeEventListener(
        "resize",
        updateMobileWebViewHeight,
      );
      window.visualViewport?.removeEventListener(
        "scroll",
        updateMobileWebViewHeight,
      );
      window.removeEventListener("resize", updateMobileWebViewHeight);
      window.removeEventListener(
        "orientationchange",
        resetKeyboardViewportBaseline,
      );
      document.removeEventListener("focusin", handleFocusIn);
      document.removeEventListener("focusout", handleFocusOut);
      clearKeyboardCloseTimeout();
      document.documentElement.style.removeProperty(
        "--hope-visible-viewport-height",
      );
      document.documentElement.style.removeProperty("--mobile-webview-height");
    };
  }, [isMobile]);

  useEffect(() => {
    const chatBody = chatBodyRef.current;

    if (!chatBody) {
      return undefined;
    }

    const markTranscript = () => {
      chatBody
        .querySelectorAll("[data-hope-transcript]")
        .forEach((element) => {
          element.removeAttribute("data-hope-transcript");
        });

      const scrollableTranscript = chatBody.querySelector(
        ".webchat__basic-transcript__scrollable",
      );
      const transcriptRoot = chatBody.querySelector(
        ".webchat__basic-transcript",
      );
      const transcriptElement = scrollableTranscript || transcriptRoot;

      transcriptRoot?.setAttribute("data-hope-transcript-root", "");
      transcriptElement?.setAttribute("data-hope-transcript", "");

      if (isAgeVerificationResolved) {
        chatBody
          .querySelectorAll("[data-hope-age-verification-activity]")
          .forEach((marker) => {
            const activityElement =
              marker.closest("article.webchat__basic-transcript__activity") ||
              marker.querySelector("article.webchat__basic-transcript__activity");

            activityElement?.setAttribute(
              "data-hope-age-verification-hidden",
              "true",
            );
          });
      }
    };

    markTranscript();

    const observer = new MutationObserver(markTranscript);
    observer.observe(chatBody, {
      childList: true,
      subtree: true,
    });

    return () => {
      observer.disconnect();
    };
  }, [directLine, isAgeVerificationResolved]);

  const getTranscriptConversationId = () =>
    directLineRef.current?.conversationId ||
    directLineConversationIdRef.current ||
    currentSearchParams.get("conversationId") ||
    "";

  const persistUserActivity = (activity, clientActivityID) => {
    if (
      !isAuthenticatedAndroidSessionRef.current ||
      activity?.type !== "message"
    ) {
      return;
    }

    const conversationId = getTranscriptConversationId();
    if (!conversationId || activity?.from?.role === "bot") {
      return;
    }

    const userActivity = {
      ...activity,
      conversation: activity.conversation || { id: conversationId },
      from: { ...activity.from, role: "user" },
    };
    persistUserTranscriptActivity(
      getSessionStorage(),
      conversationId,
      userActivity,
      clientActivityID,
    );
  };
  persistUserActivityRef.current = persistUserActivity;

  const store = useMemo(
    () =>
      createStore({}, () => (next) => (action) => {
        if (action.type === ACTIONS.POST_ACTIVITY) {
          const activity = action.payload?.activity;
          if (activity?.type === "message") {
            setIsLocalTyping(true);
            persistUserActivityRef.current(
              activity,
              action.meta?.clientActivityID,
            );
          }
        }
        if (action.type === ACTIONS.CONNECTION_FULLFILLED) {
          console.info("[IRIS handoff] Direct Line connection fulfilled.");
          setIsDirectLineConnected(true);
        }
        if (action.type === ACTIONS.INCOMMING_ACTIVITY) {
          const { activity } = action.payload;
          if (
            action.meta?.source !== "transcript-restore" &&
            activity?.type === "message" &&
            activity?.from?.role === "user"
          ) {
            persistUserActivityRef.current(
              activity,
              activity.channelData?.clientActivityID,
            );
          }
          const activityConversationId = getActivityConversationId(activity);
          if (activityConversationId) {
            setDirectLineConversationId(activityConversationId);
          }
          if (activity?.from?.role === "bot") {
            setIsLocalTyping(false);
          }
          action.payload.activity.attachments =
            action.payload.activity.attachments?.filter((attachment) => {
              if (
                attachment.content?.subtype === "schedule_timeslot_selection"
              ) {
                setSlots((slots) => [...slots, ...attachment.content.actions]);
              }
              return !(
                (attachment.contentType ===
                  "application/vnd.microsoft.card.adaptive" &&
                  attachment.content?.subtype ===
                    "schedule_timeslot_selection" &&
                  attachment.content?.isLast === "false") ||
                (attachment.content?.subtype === "account_sign_in" &&
                  (isAndroid ? clientInfoRef.current : clientInfo).clientType ===
                    "account")
              );
            });

          const closeOptions = resolveBotCloseChatOptions(
            activity,
            pendingCloseOptionsRef.current,
          );
          if (closeOptions) {
            autoCloseChat(closeOptions);
          }
          if (activity.name === EVENTS.AGENT_JOINED) {
            sessionIdRef.current = activity.value.SessionId;
            setSessionId(sessionIdRef.current);
          }
          if (activity.name === EVENTS.SURVEY_URL) {
            const normalizedSurveyURL = normalizeSurveyUrl(activity.value);
            if (normalizedSurveyURL) {
              console.info("Received a valid survey URL.");
              surveyURLRef.current = normalizedSurveyURL;
              setSurveyURL(normalizedSurveyURL);
            } else {
              console.warn("Rejected an invalid survey URL.");
              surveyURLRef.current = "";
              setSurveyURL("");
            }
          }
          if (activity.name === EVENTS.ENABLE_INPUT) {
            console.log("came to enable");

            clearIrisVerificationTimeout();
            setIsIrisVerificationPending(false);
            setIsAgeVerificationResolved(true);
            setIsInputDisabled(false);
            setInputPlaceholder(ENABLED_TEXT_INPUT_PLACEHOLDER);
          }
          if (activity.name === EVENTS.DISABLE_INPUT) {
            clearIrisVerificationTimeout();
            setIsIrisVerificationPending(false);
            setIsAgeVerificationResolved(true);
            setIsInputDisabled(true);
            setInputPlaceholder(DISABLED_TEXT_INPUT_PLACEHOLDER);
          }
        }
        const result = next(action);

        if (
          action.type === ACTIONS.INCOMMING_ACTIVITY &&
          isAndroidBotMessage({
            activity: action.payload?.activity,
            isAndroid,
          })
        ) {
          androidBotScrollFrameRef.current =
            scheduleAndroidBotScroll({
              activity: action.payload.activity,
              getChatBody: () => chatBodyRef.current,
              isAndroid,
              pendingFrameId: androidBotScrollFrameRef.current,
            });
        }

        return result;
      }),
    isAndroid ? [isAndroid] : [isAndroid, clientInfo],
  );

  useEffect(() => {
    if (
      !isAuthenticatedAndroidSession ||
      !isDirectLineConnected ||
      !directLineConversationId ||
      restoredTranscriptConversationRef.current === directLineConversationId
    ) {
      return;
    }

    restoredTranscriptConversationRef.current = directLineConversationId;
    const storedActivities = readUserTranscriptActivities(
      getSessionStorage(),
      directLineConversationId,
    );

    storedActivities.forEach((activity) => {
      // Dispatching INCOMING_ACTIVITY restores the local transcript only; it
      // does not call Direct Line or send the message to the bot again.
      store.dispatch({
        type: ACTIONS.INCOMMING_ACTIVITY,
        meta: { source: "transcript-restore" },
        payload: {
          activity: {
            ...activity,
            conversation: activity.conversation || {
              id: directLineConversationId,
            },
            from: { ...activity.from, role: "user" },
          },
        },
      });
    });

    if (storedActivities.length) {
      console.info("[Transcript restore] Restored user activities after reconnect.", {
        conversationId: directLineConversationId,
        count: storedActivities.length,
      });
    }
  }, [
    directLineConversationId,
    isAuthenticatedAndroidSession,
    isDirectLineConnected,
    store,
  ]);

  const styleOptions = {
    autoScrollSnapOnPage: false,
    hideUploadButton: true,
    backgroundColor: "var(--CUI2-neutral-surface, #F8FAFB)",
    bubbleBackground: "none",
    bubbleBorderRadius: 0,
    bubbleBorderColor: "transparent",
    bubbleBorderStyle: "solid",
    bubbleBorderWidth: 1,
    bubbleTextColor: "#192129",
    bubbleNubSize: 0,
    bubbleNubOffset: "bottom",
    avatarBorderRadius: "50%",
    avatarSize: 32,
    botAvatarImage: "botAvatar.png",
    botAvatarInitials: "CA",
    userAvatarImage: "userAvatar.png",
    userAvatarInitials: "PT",
    bubbleFromUserBackground:
      "var(--azure-chatbot-user-bubble-background, #006298)",
    bubbleFromUserBorderColor:
      "var(--azure-chatbot-user-bubble-background, #006298)",
    bubbleFromUserBorderRadius: "8px 8px 0 8px",
    bubbleFromUserBorderStyle: "solid",
    bubbleFromUserBorderWidth: 1,
    bubbleFromUserNubOffset: 0,
    bubbleFromUserNubSize: undefined,
    bubbleFromUserTextColor: "White",
    microphoneButtonColorOnDictate: "#F33",
    sendBoxBackground: "var(--CUI2-neutral-surface, #F8FAFB)",
    sendBoxButtonColor: "var(--azure-chatbot-sendbox-button-color, #006298)",
    sendBoxButtonColorOnDisabled: "#F8FAFB",
    sendBoxButtonColorOnFocus:
      "var(--azure-chatbot-sendbox-button-color, #006298)",
    sendBoxButtonColorOnHover:
      "var(--azure-chatbot-sendbox-button-color-hover, #118AC7)",
    sendBoxDisabledTextColor: "#98A8B8",
    sendBoxHeight: 44,
    sendBoxMaxHeight: 216,
    sendBoxTextColor: "#192129",
    transcriptTerminatorFontSize: 8,
    timestampFormat: "relative",
    groupTimestamp: 180000,
    sendBoxBorderBottom: undefined,
    sendBoxBorderTop: "none",
    sendBoxTextWrap: true,
    sendBoxButtonAlignment: "stretch",
  };

  const sendBoxMiddleware = useMemo(
    () => [
      () => (next) => (request) => {
        const SendBoxComponent = next(request);
        if (!SendBoxComponent) {
          return null;
        }
        return (props) => (
          <SendBoxWithCharacterCount
            {...props}
            classes={classes}
            isChicagoBrand={isChicagoBrand}
            SendBoxComponent={SendBoxComponent}
          />
        );
      },
    ],
    [classes, isChicagoBrand],
  );

  const typingIndicatorMiddleware = useMemo(
    () => [
      () =>
        () =>
        ({ visible }) =>
          visible || isLocalTyping ? (
            <BubbleTypingIndicator
              classes={classes}
              avatarURL={hopeChatBotAvatar}
            />
          ) : (
            false
          ),
    ],
    [classes, hopeChatBotAvatar, isLocalTyping],
  );

  const setUserInteraction = () => {
    if (!userInteracted) {
      emitData({
        type: EVENTS.USER_INTERACTED,
      });
      setUserInteracted(true);
    }
  };

  const sendEvent = (data) => {
    const eventData = {
      from: {
        id: "YOUR_USER_ID",
        name: "user",
      },
      type: "event",
      name: data.name,
      value: {
        messageId: data.messageId,
        reactionType: data.value,
      },
    };
    directLine.postActivity(eventData).subscribe(
      (id) => {
        setUserInteraction();
      },
      (error) => console.log(error),
    );
  };

  const activityMiddleware = useMemo(
    () => [
      () => (next) => (...args) => {
        const [{ activity }] = args;
        const activityText = activity?.text;
        // Remove the resolved age-verification activity, not just its card,
        // so Web Chat does not leave an empty avatar/timestamp row behind.
        if (
          isResolvedAgeVerificationActivity(
            activity,
            isAgeVerificationResolved,
          )
        ) {
          return false;
        }
        if (
          activity?.from?.role === "user" &&
          typeof activityText === "string" &&
          activityText.length > CHARACTER_LIMIT
        ) {
          return false;
        }
        const renderActivity = next(...args);
        if (!renderActivity) {
          return renderActivity;
        }

        const isAgeVerificationActivity = activity?.attachments?.some(
          (attachment) =>
            attachment?.content?.subtype === "age_verification",
        );

        if (isAgeVerificationActivity) {
          return (renderAttachment, props) => (
            <div data-hope-age-verification-activity="true">
              {renderActivity(renderAttachment, props)}
            </div>
          );
        }

        const shouldShowReactions =
          activity?.type === "message" &&
          activity?.from?.role === "bot" &&
          activity?.reactionsAdded?.length;

        if (!shouldShowReactions) {
          return renderActivity;
        }

        const attachmentText = activity?.attachments?.find(
          (attachment) => typeof attachment?.content === "string"
        )?.content;
        const messageText =
          typeof activityText === "string" ? activityText : attachmentText || "";
        const messageId = activity?.replyToId || activity?.id;
        const statusClassName = [
          classes.activityStatusContent,
          activity?.from?.role === "user" && classes.activityStatusContentFromUser,
        ]
          .filter(Boolean)
          .join(" ");

        const handleReactionEvent = (reactionType) => {
          if (!messageId) {
            return;
          }
          sendEvent({
            name: EVENTS.MESSAGE_REACTION,
            value: reactionType,
            messageId,
          });
        };

        return (renderAttachment, props) => {
          const { renderActivityStatus, ...restProps } = props || {};
          const wrappedRenderActivityStatus = ({ hideTimestamp }) => (
            <div className={statusClassName}>
              <Reactions
                handleReactionEvent={handleReactionEvent}
                reactions={activity.reactionsAdded}
                messageText={messageText}
              />
              {typeof renderActivityStatus === "function" &&
                renderActivityStatus({ hideTimestamp })}
            </div>
          );

          return renderActivity(renderAttachment, {
            ...restProps,
            renderActivityStatus: wrappedRenderActivityStatus,
          });
        };
      },
    ],
    [classes, isAgeVerificationResolved, sendEvent],
  );

  useEffect(() => {
    const abortController = new AbortController();
    const conversation = isMobile
      ? getMobileConversationId()
      : readStoredConversation();
    const conversationStoredTime = conversation?.time;
    const timeDiffrence = new Date().getTime() - conversationStoredTime;
    const timeDiffInSeconds = timeDiffrence / 1000;
    const conversationId =
      timeDiffInSeconds < 300 && conversation?.id ? conversation.id : "";
    const reconnect = async () => {
      try {
        const connect = (requestedConversationId) =>
          fetchDirectLineConnection({
            authorization: process.env.REACT_APP_FUNCTION_APP_KEY,
            fallbackConversationId: requestedConversationId,
            signal: abortController.signal,
            url: `${
              process.env.REACT_APP_FUNCTION_APP_URL + `/DirectLineToken`
            }${
              requestedConversationId
                ? `/?conversationId=${requestedConversationId}`
                : ""
            }`,
          });
        const connection = await reconnectWithIosStorageRecovery({
          connect,
          conversationId,
          isMobile,
          platform: getPlatform(),
          signal: abortController.signal,
        });

        if (!connection) {
          return;
        }

        const webSocketEnabled = !isSafariBrowser();
        setIsDirectLineConnected(false);
        setDirectLineConversationId(connection.conversationId);
        setDirectline(
          createDirectLine({
            conversationId: connection.conversationId || undefined,
            token: connection.token,
            streamUrl: connection.streamUrl,
            webSocket: webSocketEnabled,
          }),
        );
        if (!webSocketEnabled) {
          console.warn(
            "Safari detected; disabling Direct Line WebSocket and falling back to polling.",
          );
        }
      } catch (error) {
        if (error?.name === "AbortError") {
          return;
        }
        console.error("Error reconnecting to conversation:", error);
      }
    };

    reconnect();

    window.addEventListener("message", setMetaData);
    return () => {
      abortController.abort();
      window.removeEventListener("message", setMetaData);
    };
  }, []);

  useEffect(() => {
    if (!isLocalTyping) {
      if (localTypingTimeoutRef.current) {
        clearTimeout(localTypingTimeoutRef.current);
        localTypingTimeoutRef.current = null;
      }
      return;
    }

    if (localTypingTimeoutRef.current) {
      clearTimeout(localTypingTimeoutRef.current);
    }

    localTypingTimeoutRef.current = setTimeout(() => {
      setIsLocalTyping(false);
      localTypingTimeoutRef.current = null;
    }, 15000);

    return () => {
      if (localTypingTimeoutRef.current) {
        clearTimeout(localTypingTimeoutRef.current);
        localTypingTimeoutRef.current = null;
      }
    };
  }, [isLocalTyping]);

  useEffect(() => {
    if (
      !directLine ||
      !isDirectLineConnected ||
      clientPlatformInfoSentDirectLineRef.current === directLine
    ) {
      return;
    }

    if (
      sendClientPlatformInfoOnConnection({
        directLine,
        send: sendClientPlatformInfo,
      })
    ) {
      clientPlatformInfoSentDirectLineRef.current = directLine;
    }
  }, [directLine, isDirectLineConnected]);

  useEffect(() => {
    if (directLine) {
       console.log("Sending ClientContextUrl", clientContextUrl);
      sendClientContextUrl(directLine, clientContextUrl);
    }
  }, [clientContextUrl, directLine]);

  useEffect(() => {
    if (!irisHandoffCode) {
      return;
    }

    if (!directLine) {
      console.info("[IRIS handoff] Waiting for Direct Line instance before sending.");
      return;
    }

    if (!isDirectLineConnected) {
      console.info(
        "[IRIS handoff] Waiting for Direct Line connection before sending.",
      );
      return;
    }

    if (irisHandoffSentRef.current) {
      console.info("[IRIS handoff] handoff_code event already sent.");
      return;
    }

    if (irisHandoffPostDirectLineRef.current === directLine) {
      console.info("[IRIS handoff] handoff_code event send already in progress.");
      return;
    }

    if (irisHandoffPostDirectLineRef.current) {
      console.warn(
        "[IRIS handoff] Retrying handoff_code event with the latest Direct Line instance.",
      );
    }

    irisHandoffPostDirectLineRef.current = directLine;
    console.info("[IRIS handoff] Sending handoff_code event through Direct Line.");
    directLine
      .postActivity({
        from: { id: "user1", name: "user" },
        type: "event",
        name: EVENTS.IRIS_HANDOFF_CODE,
        value: {
          metadata: {
            handoff_code: irisHandoffCode,
          },
        },
      })
      .subscribe(
        (activityId) => {
          if (!isAcceptedDirectLineActivityId(activityId)) {
            irisHandoffPostDirectLineRef.current = null;
            console.warn(
              "[IRIS handoff] Direct Line did not accept the handoff_code event.",
              { activityId },
            );
            return;
          }

          irisHandoffSentRef.current = true;
          irisHandoffPostDirectLineRef.current = null;
          try {
            getSessionStorage()?.setItem(
              AUTHENTICATED_SESSION_STORAGE_KEY,
              "true",
            );
          } catch {
            // Authentication state is best effort; the handoff already succeeded.
          }
          console.info(
            "[IRIS handoff] handoff_code event accepted by Direct Line.",
            { activityId },
          );
          sendClientPlatformInfoAfterHandoff({
            conversationId: resolveDirectLineConversationId(
              directLineConversationId,
              directLine,
            ),
            directLine,
            handoffAccepted: true,
            send: sendClientPlatformInfo,
            shouldSend: shouldSendClientPlatformInfoRef.current,
          });
          returnToMainRouteAfterIrisHandoff();
          setIrisHandoffCode("");
        },
        (error) => {
          clearIrisVerificationTimeout();
          setIsIrisVerificationPending(false);
          if (irisHandoffPostDirectLineRef.current === directLine) {
            irisHandoffPostDirectLineRef.current = null;
          }
          console.error(
            "[IRIS handoff] Failed to send handoff_code event through Direct Line.",
            error,
          );
          sendClientPlatformInfoAfterHandoff({
            conversationId: resolveDirectLineConversationId(
              directLineConversationId,
              directLine,
            ),
            directLine,
            handoffAccepted: false,
            send: sendClientPlatformInfo,
            shouldSend: shouldSendClientPlatformInfoRef.current,
          });
        },
      );
  }, [
    irisHandoffCode,
    directLine,
    directLineConversationId,
    isDirectLineConnected,
  ]);

function setMetaData(e) {
  console.log("came to set metadata");
  console.log(e);
  const messageType = e?.data?.type;
  
  if (messageType === EVENTS.CLIENT_INFO) {
    console.log("came to set client info");
    setClientInfo(e.data.payload);
  }
  
  if (messageType === EVENTS.CLIENT_CONTEXT_URL) {
    const sourceUrl = e?.data?.payload?.metadata?.sourceUrl;
    if (typeof sourceUrl === "string" && sourceUrl)
      setClientContextUrl(sourceUrl);
  }
}
  //Exposing to window so that mobile can access it.
  window.setMetaData = setMetaData;

  function getMobileConversationId() {
    return {
      id: currentSearchParams.get("conversationId"),
      time: currentSearchParams.get("time"),
    };
  }

  const postMetadataEvent = (
    directLine,
    name,
    metadata,
    successMessage,
    callbacks = {},
  ) => {
    const payload = {
      from: { id: "user1", name: "user" },
      type: "event",
      name,
      value: {
        metadata,
      },
    };

    directLine.postActivity(payload).subscribe(
      (activityId) => {
        if (successMessage) {
          console.log(successMessage);
        }
        callbacks.onSuccess?.(activityId);
      },
      (error) => {
        console.log(error);
        callbacks.onError?.(error);
      },
    );
  };

  const sendClientPlatformInfo = (directLine, appContext, callbacks) => {
    const platform = getPlatform();

    postMetadataEvent(
      directLine,
      EVENTS.CLIENT_PLATFORM_INFO,
      {
        UserAgent: getUserBrowserDetails().userAgent,
        platform,
        client: platform,
        deviceType: platform,
        clientType: platform === "web" ? "web" : "mobile",
        appContext,
      },
      "Successfully sent client platform info",
      callbacks,
    );
  };

  const sendClientContextUrl = (directLine, sourceUrl) => {

    if (typeof sourceUrl !== "string" || !sourceUrl) {
      return;
    }
    if (isChicagoBrand) {
      try {
        const contextUrl = new URL(sourceUrl);
        contextUrl.searchParams.set("brand", "chicago");
        sourceUrl = contextUrl.href;
      } catch {
        // Preserve the existing payload if the host supplies an invalid URL.
      }
    }
    postMetadataEvent(
      directLine,
      EVENTS.CLIENT_CONTEXT_URL,
      { sourceUrl },
      "Successfully sent client context url",
    );
  };

  const setConversationId = (id) => {
    const conversationId = {
      id: id,
      time: new Date().getTime(),
    };
    if (isMobile) {
      emitData({ type: EVENTS.CONVERSATION_STARTED, payload: conversationId });
    } else {
      localStorage?.setItem("conversationId", JSON.stringify(conversationId));
    }
  };

  function receiveMessage(message) {
    // Handle the message
    console.log("Received from native:", message);
    setHeaderMessage(message);
  }

  window.receiveMessage = receiveMessage;

  const attachmentMiddleware = () => (next) => (card) => {
    if (card.activity.conversation) {
      setConversationId(card.activity.conversation?.id);
    }
    if (card.activity.from.role === "bot") {
      const isAgeVerificationActivity = card.activity?.attachments?.some(
        (attachment) =>
          attachment?.content?.subtype === "age_verification",
      );

      if (isAgeVerificationActivity && isIrisVerificationPending) {
        return (
          <div className={classes.irisVerificationPending} role="status">
            Verifying your account...
          </div>
        );
      }

      return (
        <CustomCard
          card={card}
          next={next}
          handleChipClick={sendMessage}
          slots={slots}
          isAgeVerificationResolved={isAgeVerificationResolved}
          minimizeBot={() => {
            if (clientInfo.clientType === "account") {
              setIsMinimized((prev) => !prev);
            }
          }}
        />
      );
    }
    return next(card);
  };

  const avatarMiddleware = () => (next) => (card) => {
    const { fromUser, ...props } = card;
    const { MessageType, AgentFirstName } =
      props.activity.channelData || {};
    const isSurveyOpenNewTabActivity = (activity) =>
      activity?.attachments?.some((attachment) => {
        if (
          attachment?.contentType !== "application/vnd.microsoft.card.adaptive"
        ) {
          return false;
        }

        const content = attachment?.content;
        if (content?.subtype !== "open_new_tab_subtype") {
          return false;
        }

        const isOpenUrlAction = (action) =>
          action?.type?.toLowerCase() === "action.openurl";
        const hasSurveyKeyword = (value) =>
          typeof value === "string" && value.toLowerCase().includes("survey");
        const openUrlActions = [
          ...(content?.actions || []),
          ...(content?.body?.flatMap((item) => item?.actions || []) || []),
        ].filter(isOpenUrlAction);

        if (!openUrlActions.length) {
          return false;
        }

        return (
          openUrlActions.some(
            (action) =>
              hasSurveyKeyword(action?.title) ||
              hasSurveyKeyword(action?.text) ||
              hasSurveyKeyword(action?.url),
          ) ||
          content?.body?.some((item) => hasSurveyKeyword(item?.text))
        );
      });

    return () => {
      if (
        isResolvedAgeVerificationActivity(
          props.activity,
          isAgeVerificationResolvedRef.current,
        )
      ) {
        return null;
      }

      if (fromUser) {
        setUserInteraction();
        if (props.activity.from.name) {
          return (
            <Avatar style={defaultAvatarStyles}>
              {getInitialsFromFirstNameOrFullNameAndLastName(
                props.activity.from.name,
              )}
            </Avatar>
          );
        }
        return (
          <img
            src={guestAvatarURL}
            alt="Bot Avatar"
            style={defaultAvatarStyles}
          />
        );
      }

      if (MessageType === "Agent" || card.activity.from.role === "bot") {
        if (isSurveyOpenNewTabActivity(props.activity)) {
          return (
            <img
              src={adventLogoURL}
              alt="Advent Health"
              style={surveyAvatarStyles}
            />
          );
        }

        if (AgentFirstName) {
          return (
            <Avatar style={defaultAvatarStyles}>
              {AgentFirstName.trim().charAt(0).toUpperCase()}
            </Avatar>
          );
        }

        return (
          <img
            src={hopeChatBotAvatar}
            alt="Hope Avatar"
            style={defaultAvatarStyles}
          />
        );
      }

      return next(card)();
    };
  };

  const groupActivitiesMiddleware =
    () =>
    () =>
    (next) =>
    ({ activities }) => {
      // Ensure no messages are grouped; TODO: Modify grouping logic
      const sender = activities.map((activity) => [activity]);
      const status = activities.map((activity) => [activity]);

      return {
        sender,
        status,
      };
    };

  const showCloseModalWindow = () => {
    if (isMinimized) {
      onMinimizeClick();
    }
    setShowCloseModal(true);
  };

  const finishNativeSurveyClose = () => {
    nativeSurveyReturnCleanupRef.current();
    nativeSurveyReturnCleanupRef.current = () => {};

    if (emitMobileCloseBot()) {
      setShowNativeCloseFallback(false);
    }
  };

  const armIosSurveyReturnClose = () => {
    nativeSurveyReturnCleanupRef.current();

    const handleSurveyReturn = () => finishNativeSurveyClose();
    const cleanup = () => window.removeEventListener("focus", handleSurveyReturn);

    nativeSurveyReturnCleanupRef.current = cleanup;
    window.addEventListener("focus", handleSurveyReturn, { once: true });
  };

  const closeChat = ({ redirectUrl = "", isNativeApp = false } = {}) => {
    setShowCloseModal(false);
    const isNativeClose = isNativeApp || isNativeAppAtClose();
    const shouldOpenAuthenticatedNativeSurveyLink =
      isAuthenticatedHopeSession &&
      isNativeClose &&
      !hasNativeSurveyLaunchBridge();
    const surveyAlreadyPresented = shouldOpenAuthenticatedNativeSurveyLink
      ? launchValidatedSurveyAnchor(surveyURLRef.current)
      : false;
    if (!isNativeClose) {
      const launchSurvey = isAuthenticatedHopeSession
        ? launchValidatedSurveyAnchor
        : launchValidatedSurveyLink;
      launchSurvey(surveyURLRef.current);
    }

    const closeOptions = {
      redirectUrl,
      isNativeApp: isNativeClose,
      surveyAlreadyPresented,
    };
    pendingCloseOptionsRef.current = closeOptions;
    const closeActivity = createCloseChatActivity("user1");

    try {
      const closeRequest = directLine?.postActivity(closeActivity);

      if (isAndroid) {
        // Android must not wait for Direct Line before closing the native chat.
        closeRequest?.subscribe(
          () => console.info("Close-chat event accepted by Direct Line."),
          (error) => console.error("Close-chat event failed.", error),
        );
        autoCloseChat(closeOptions);
        return;
      }

      closeRequest?.subscribe(
        () => autoCloseChat(closeOptions),
        (error) => console.error("Close-chat event failed.", error),
      );
    } catch (error) {
      console.error("Unable to send close-chat event.", error);
      if (isAndroid) {
        autoCloseChat(closeOptions);
      }
    }
  };

  const autoCloseChat = ({
    redirectUrl = "",
    isNativeApp = false,
    surveyAlreadyPresented = false,
  } = {}) => {
    if (closeHandledRef.current) {
      return;
    }
    closeHandledRef.current = true;
    pendingCloseOptionsRef.current = null;
    const conversationId = getTranscriptConversationId();

    //Need to remove this if condition once Drupal make changes.
    const latestSessionId = sessionIdRef.current;
    if (latestSessionId) {
      emitData({
        type: EVENTS.AGENT_LEFT,
        payload: {
          sessionKey: latestSessionId,
        },
      });
    }

    console.info(
      "Closing chat with a validated survey URL:",
      Boolean(surveyURLRef.current),
    );

    // Native survey presentation can suspend the WebView immediately. Commit
    // the closed UI first so dismissing the survey cannot reveal a stale,
    // non-interactive End Chat dialog underneath it.
    flushSync(() => {
      setShowCloseModal(false);
      setIsChatClosed(true);
      setShowNativeCloseFallback(false);
    });

    const deliveryResult = completeChatClose({
      closeNative: emitMobileCloseBot,
      emitEvent: (event) =>
        deliverChatClosedEvent(event, { surveyAlreadyPresented }),
      getSessionId: () => sessionIdRef.current,
      getSurveyURL: () => surveyURLRef.current,
      redirectUrl,
      sessionId,
      surveyURL,
    });
    const shouldShowNativeCloseFallback =
      deliveryResult?.deferNativeClose === true;
    flushSync(() => {
      setShowNativeCloseFallback(shouldShowNativeCloseFallback);
    });
    if (
      shouldShowNativeCloseFallback &&
      window.webkit?.messageHandlers
    ) {
      armIosSurveyReturnClose();
    }
    if (redirectUrl) {
      markHopeSessionCleanupPending(conversationId);
    } else {
      clearUserTranscriptActivities(getSessionStorage(), conversationId);
      try {
        getSessionStorage()?.removeItem(AUTHENTICATED_SESSION_STORAGE_KEY);
      } catch {
        // Storage cleanup is best effort; native close must still complete.
      }
      localStorage?.removeItem("conversationId");
    }
    if (redirectUrl) {
      redirectAfterEndChat({ isNativeApp, redirectUrl });
    }
  };

  const handleEndChat = () => {
    // Native bridges can be injected after the initial React render, so check
    // for them when the user actually closes the chat.
    const isNativeApp = isNativeAppAtClose();
    const authenticatedRedirectUrl = isNativeApp
      ? ""
      : AUTHENTICATED_WEB_END_CHAT_URL;

    closeChat(
      isAuthenticatedHopeSession
        ? { redirectUrl: authenticatedRedirectUrl, isNativeApp }
        : undefined,
    );
  };

  const onMinimizeClick = () => {
    if (!isMinimized) {
      window.parent?.postMessage({ type: "CHAT_MINIMIZED" }, "*");
      window.android?.minimizeBot();
      window.webkit?.messageHandlers?.minimizeBot?.postMessage(null);
    } else {
      window.parent?.postMessage({ type: "CHAT_MAXIMIZED" }, "*");
      window.android?.maximizeBot();
      window.webkit?.messageHandlers?.maximizeBot?.postMessage(null);
    }
    setIsMinimized((prev) => !prev);
  };

  const minimizeChat = () => {
    return (
      <IconButton
        aria-label="minimize chatbox"
        className={classes.iconButton}
        onClick={onMinimizeClick}
      >
        {!isMinimized ? (
          <img
            src={imageAssetUrl("closefullscreen.svg")}
            alt=""
            className={classes.closeFullscreenIcon}
          />
        ) : (
          <img
            src={imageAssetUrl("openfullscreen.svg")}
            alt=""
            className={classes.closeFullscreenIcon}
          />
        )}
      </IconButton>
    );
  };

  const getHeader = () => {
    return !isMobile ? (
      <Paper square className={classes.chatHeader}>
        <div className={classes.accent}></div>
        <div
          className={`${classes.headerFlexContainer} ${
            isMinimized ? classes.headerFlexContainerMinimized : ""
          }`}
        >
          {isMinimized && (
            <Avatar
              className={classes.headerAvatar}
              alt="Hope Chat"
              src={hopeChatBotAvatar}
            />
          )}
          <div className={classes.headerText}>
            <Typography variant="body2">Hope</Typography>
            <Typography variant="caption">{headerMessage}</Typography>
          </div>
          <div className={classes.headerIcons}>
            {!isMinimized && menuDropdown()}
            {!shouldHideWindowControls && minimizeChat()}
            {!shouldHideWindowControls && (
              <IconButton
                className={classes.iconButton}
                aria-label="close chatbox"
                onClick={showCloseModalWindow}
              >
                <img
                  src={imageAssetUrl("close.svg")}
                  alt=""
                  className={classes.closeIcon}
                />
              </IconButton>
            )}
          </div>
        </div>
      </Paper>
    ) : (
      <Paper square className={`${classes.chatHeader} ${classes.mobileHeader}`}>
        <div className={classes.mobileHeaderContainer}>
          <div className={classes.mobileHeaderSide}>
            {!isMinimized && menuDropdown()}
          </div>
          <div className={classes.mobileHeaderText}>
            <Typography variant="body2">Hope</Typography>
            <Typography variant="caption">{headerMessage}</Typography>
          </div>

          <div
            className={`${classes.mobileHeaderSide} ${classes.mobileHeaderIcons}`}
          >
            {!shouldHideWindowControls && (
              <IconButton
                className={classes.iconButton}
                aria-label="close chatbox"
                onClick={showCloseModalWindow}
              >
                <img
                  src={imageAssetUrl("close.svg")}
                  alt=""
                  className={classes.closeIcon}
                />
              </IconButton>
            )}
          </div>
        </div>
      </Paper>
    );
  };

  const openMenu = (event) => {
    setAnchorEl((current) => (current ? null : event.currentTarget));
  };

  const closeMenu = () => {
    setAnchorEl(null);
  };

  const selectItem = (item) => {
    sendMessage(item);
    closeMenu();
  };

  const sendMessage = (message) => {
    const clientActivityID = isAuthenticatedAndroidSession
      ? createClientActivityID()
      : undefined;
    const data = {
      from: { id: "user1", name: "user" },
      textFormat: "plain",
      channelData: {
        metaData: {
          UserAgent: getUserBrowserDetails().userAgent,
          platform: getPlatform(),
        },
        ...(clientActivityID ? { clientActivityID } : {}),
      },
    };
    if (message.data.linkSelected) {
      data.type = "event";
      data.value = message.data.linkSelected;
      data.name = "linkselected";
    } else {
      data.type = "message";
      data.value = message.data;
      persistUserActivity(data, clientActivityID);
    }
    directLine.postActivity(data).subscribe(
      (id) => {
        setUserInteraction();
      },
      (error) => console.log(error),
    );
  };

  const handleChatBodyClick = (event) => {
    const link = event.target.closest("a");
    if (!link) {
      return;
    }
    const href = link.getAttribute("href")?.trim();
    if (!href) {
      return;
    }
    const linkText = link.textContent?.trim();

    sendMessage({ data: { linkSelected: linkText || href } });
  };

  const menuDropdown = () => {
    const dropdownItems = [
      {
        data: { action: "live_agent" },
        title: "Chat with a Care Advocate",
        type: "Action.Submit",
      },
    ];
    return (
      <IconButton
        aria-controls="simple-menu"
        aria-haspopup="true"
        onClick={openMenu}
      >
        <img
          src={imageAssetUrl("menu.svg")}
          alt=""
          className={classes.menuIcon}
        />
        <Menu
          className={classes.menu}
          id="simple-menu"
          anchorEl={anchorEl}
          keepMounted
          elevation={0}
          open={Boolean(anchorEl)}
          onClose={closeMenu}
          anchorOrigin={{
            vertical: "bottom",
            horizontal: "center",
          }}
          transformOrigin={{
            vertical: "top",
            horizontal: "center",
          }}
          PaperProps={{
            style: {
              boxShadow:
                "0px 5px 3px 0px rgb(25 33 41 / 4%), 0px 8px 5px 0px rgb(25 33 41 / 8%)",
            },
          }}
        >
          {dropdownItems.map((item) => (
            <MenuItem
              className={classes.menuItem}
              onClick={() => selectItem(item)}
            >
              {item.title}
            </MenuItem>
          ))}
        </Menu>
      </IconButton>
    );
  };
  const closeModal = () => {
    setShowCloseModal(false);
  };

  const chatContainerClassName = [
    classes.chatContainer,
    isMobile && classes.mobileChatContainer,
    isMobile && isKeyboardVisible && classes.mobileKeyboardOpen,
  ]
    .filter(Boolean)
    .join(" ");

  return !isChatClosed ? (
    <div className={chatContainerClassName} data-hope-chat-sheet>
      {getHeader()}

        <div
          className={classes.disclaimer}
          style={{
            display: isMinimized || isKeyboardVisible ? "none" : "flex",
          }}
        >
        <p className={classes.disclaimerItem}>
          Welcome to Hope, your virtual assistant. This chat is intended for
          informational and educational purposes for ages 18+. To learn more,{" "}
          <a
            className={classes.disclaimerLink}
            href="https://www.adventhealth.com/legal/privacy-policy"
            target="_blank"
            rel="noopener noreferrer"
          >
            view our terms of service.
          </a>
        </p>
        <p className={`${classes.disclaimerItem} ${classes.emergencyBanner}`}>
          If this is an emergency, call 911 or go to the nearest emergency room.
        </p>
      </div>

      {directLine && (
        <div
          ref={chatBodyRef}
          className={classes.chatBody}
          onClick={handleChatBodyClick}
          style={{ display: isMinimized ? "none" : "flex" }}
        >
          <ReactWebChat
            directLine={directLine}
            store={store}
            styleOptions={styleOptions}
            disabled={isInputDisabled}
            userID="YOUR_USER_ID"
            attachmentMiddleware={attachmentMiddleware}
            avatarMiddleware={avatarMiddleware}
            cardActionMiddleware={cardActionMiddleware}
            htmlContentTransformMiddleware={htmlContentTransformMiddleware}
            groupActivitiesMiddleware={groupActivitiesMiddleware}
            activityMiddleware={activityMiddleware}
            sendBoxMiddleware={sendBoxMiddleware}
            typingIndicatorMiddleware={typingIndicatorMiddleware}
            overrideLocalizedStrings={{
              TEXT_INPUT_PLACEHOLDER: inputPlaceholder,
            }}
          />
        </div>
      )}
      {showCloseModal && (
        <div className={classes.closeModalOverlay}>
          <div
            className={classes.closeModalDialog}
            role="dialog"
            aria-modal="true"
            aria-labelledby="end-chat-title"
            aria-describedby="end-chat-body"
          >
            <div className={classes.closeModalContent}>
              <div id="end-chat-title" className={classes.closeModalTitle}>
                End Chat
              </div>
              <div id="end-chat-body" className={classes.closeModalBody}>
                <p className={classes.closeModalText}>
                  Are you sure you'd like to end your chat session?
                </p>
                <p className={classes.closeModalText}>
                  If you haven't already completed our{" "}
                  {surveyURL ? (
                    <a
                      className={classes.closeModalLink}
                      href={surveyURL}
                      target="_blank"
                      rel="noopener noreferrer"
                    >
                      quick survey
                    </a>
                  ) : (
                    <span className={classes.closeModalLink}>quick survey</span>
                  )}
                  , we'd love your feedback.
                </p>
              </div>
            </div>
            <div className={classes.closeModalActions}>
              <div className={classes.closeModalActionsRow}>
                <CustomButton
                  variant="outlined"
                  onClick={closeModal}
                  className={classes.cancelButton}
                >
                  Cancel
                </CustomButton>
                <CustomButton
                  variant="contained"
                  onClick={handleEndChat}
                  className={classes.endChatButton}
                >
                  End Chat
                </CustomButton>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  ) : showNativeCloseFallback ? (
    <div className={classes.closeModalOverlay}>
      <div className={classes.closeModalDialog} role="status">
        <div className={classes.closeModalContent}>
          <p className={classes.closeModalText}>Your chat has ended.</p>
        </div>
        <div className={classes.closeModalActions}>
          <CustomButton
            variant="contained"
            onClick={finishNativeSurveyClose}
            className={classes.endChatButton}
          >
            Close chat
          </CustomButton>
        </div>
      </div>
    </div>
  ) : null;
}
